import { cva, type VariantProps } from 'class-variance-authority'
import { Avatar as AvatarPrimitive } from 'radix-ui'
import type { ComponentProps, ReactNode } from 'react'

import { cn } from '../lib/cn'
import { toneSoftBg } from '../lib/tone'
import type { SemanticTone } from '../tokens'

/* Avatar, Skeleton, Panel (Card), PageHeader e EmptyState. */

const avatarVariants = cva(
  'relative inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full font-medium text-ink select-none',
  {
    variants: {
      size: {
        sm: 'size-6 text-micro',
        md: 'size-8 text-compact',
        lg: 'size-10 text-body',
      },
    },
    defaultVariants: { size: 'md' },
  },
)

/**
 * Iniciais do primeiro e do último nome ("Maria da Silva" → "MS"). Palavras que não começam
 * por letra ("(dev)", "2ª") são ignoradas.
 */
export function initials(name: string): string {
  const parts = name
    .trim()
    .split(/\s+/)
    .filter((part) => /^\p{L}/u.test(part))
  const first = parts[0]?.charAt(0) ?? ''
  const last = parts.length > 1 ? (parts[parts.length - 1]?.charAt(0) ?? '') : ''
  return (first + last).toUpperCase()
}

export interface AvatarProps
  extends
    Omit<ComponentProps<typeof AvatarPrimitive.Root>, 'children'>,
    VariantProps<typeof avatarVariants> {
  /** Nome completo: gera as iniciais e o texto alternativo. */
  name: string
  src?: string
  /** Cor semântica do fundo (ex.: a cor da cobradora). Padrão: petróleo. */
  tone?: SemanticTone
}

export function Avatar({ name, src, tone = 'petroleo', size, className, ...props }: AvatarProps) {
  return (
    <AvatarPrimitive.Root
      className={cn(
        avatarVariants({ size }),
        tone === 'petroleo' ? 'bg-petroleo-soft' : toneSoftBg[tone],
        className,
      )}
      {...props}
    >
      {src ? (
        <AvatarPrimitive.Image src={src} alt={name} className="size-full object-cover" />
      ) : null}
      <AvatarPrimitive.Fallback>
        <span aria-hidden>{initials(name)}</span>
        <span className="sr-only">{name}</span>
      </AvatarPrimitive.Fallback>
    </AvatarPrimitive.Root>
  )
}

/** Bloco de carregamento no formato do conteúdo final. Decorativo (aria-hidden). */
export function Skeleton({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      aria-hidden
      className={cn('animate-pulse rounded-sm bg-surface-2 motion-reduce:animate-none', className)}
      {...props}
    />
  )
}

/** Painel de conteúdo: surface + borda de 1 px, raio 12, sem sombra (elevação é borda). */
export function Panel({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      className={cn('rounded-lg border border-border bg-surface text-ink', className)}
      {...props}
    />
  )
}

export function PanelHeader({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      className={cn('flex items-start justify-between gap-4 px-5 pt-4 pb-3', className)}
      {...props}
    />
  )
}

export function PanelTitle({ className, children, ...props }: ComponentProps<'h2'>) {
  return (
    <h2 className={cn('text-body-lg font-semibold text-ink', className)} {...props}>
      {children}
    </h2>
  )
}

export function PanelDescription({ className, ...props }: ComponentProps<'p'>) {
  return <p className={cn('text-compact text-ink-2', className)} {...props} />
}

export function PanelContent({ className, ...props }: ComponentProps<'div'>) {
  return <div className={cn('px-5 pb-5', className)} {...props} />
}

export function PanelFooter({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'flex items-center justify-end gap-2 border-t border-border px-5 py-3',
        className,
      )}
      {...props}
    />
  )
}

export {
  Panel as Card,
  PanelContent as CardContent,
  PanelDescription as CardDescription,
  PanelFooter as CardFooter,
  PanelHeader as CardHeader,
  PanelTitle as CardTitle,
}

export interface PageHeaderProps extends Omit<ComponentProps<'header'>, 'title'> {
  title: ReactNode
  description?: ReactNode
  /** Ações à direita (botões). */
  actions?: ReactNode
  /** Link de voltar (`BackLink`) acima do título, sempre no mesmo lugar. */
  back?: ReactNode
}

/**
 * Cabeçalho de página: título 24/32 (h1 focável para a troca de rota), descrição em ink-2,
 * ações à direita. `children` entra abaixo (faixa de aging, resumo, abas da seção). Padrão das
 * seções com abas: título da seção, depois as abas (`children`), depois o conteúdo, sem repetir
 * o nome da aba ativa como h2.
 */
export function PageHeader({
  title,
  description,
  actions,
  back,
  className,
  children,
  ...props
}: PageHeaderProps) {
  return (
    <header className={cn('flex flex-col gap-4', className)} {...props}>
      {back ? <div className="-mb-2 flex">{back}</div> : null}
      <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
        <div className="flex max-w-[72ch] min-w-0 flex-col gap-1">
          <h1 tabIndex={-1} className="text-page text-ink outline-none">
            {title}
          </h1>
          {description ? <p className="text-body text-ink-2">{description}</p> : null}
        </div>
        {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
      </div>
      {children}
    </header>
  )
}

/** Ilustração leve: linhas de uma carteira vazia, nos tokens do tema. */
function EmptyIllustration() {
  return (
    <svg aria-hidden viewBox="0 0 120 72" className="h-18 w-30" fill="none">
      <rect
        x="0.5"
        y="0.5"
        width="119"
        height="71"
        rx="11.5"
        className="fill-surface stroke-border"
      />
      <rect x="12" y="14" width="3" height="12" rx="1.5" className="fill-petroleo" />
      <rect x="22" y="16" width="52" height="8" rx="4" className="fill-surface-2" />
      <rect x="84" y="16" width="24" height="8" rx="4" className="fill-petroleo-soft" />
      <rect x="12" y="32" width="3" height="12" rx="1.5" className="fill-border" />
      <rect x="22" y="34" width="40" height="8" rx="4" className="fill-surface-2" />
      <rect x="84" y="34" width="24" height="8" rx="4" className="fill-surface-2" />
      <rect x="12" y="50" width="3" height="12" rx="1.5" className="fill-border" />
      <rect x="22" y="52" width="46" height="8" rx="4" className="fill-surface-2" />
      <rect x="84" y="52" width="24" height="8" rx="4" className="fill-surface-2" />
    </svg>
  )
}

export interface EmptyStateProps extends Omit<ComponentProps<'div'>, 'title'> {
  title: ReactNode
  description?: ReactNode
  /** Ícone lucide no lugar da ilustração padrão. */
  icon?: ReactNode
  /** Ações (botões) vão como children: vazio é convite. */
  children?: ReactNode
}

export function EmptyState({
  title,
  description,
  icon,
  className,
  children,
  ...props
}: EmptyStateProps) {
  return (
    <div
      className={cn('flex flex-col items-center gap-4 px-6 py-12 text-center', className)}
      {...props}
    >
      {icon ? (
        <div
          aria-hidden
          className="grid size-12 place-items-center rounded-lg bg-petroleo-soft text-petroleo [&_svg]:size-6"
        >
          {icon}
        </div>
      ) : (
        <EmptyIllustration />
      )}
      <div className="flex max-w-[56ch] flex-col gap-1">
        <p className="text-body-lg font-semibold text-ink">{title}</p>
        {description ? <p className="text-body text-ink-2">{description}</p> : null}
      </div>
      {children ? <div className="flex flex-wrap justify-center gap-2">{children}</div> : null}
    </div>
  )
}
